/* eslint-disable no-nested-ternary */
import React from "react";
import { Icon, Panel, PlainButton } from "@culturehq/components";

import { useDepartments, useLocations, useQueryString, useRecognitionTypes, useValues } from "app/context";
import { useDoor, useFieldsLabels } from "app/hooks";
import { getFiltersOptions, t, Trans } from "app/utils";

import FilterGroup from "components/common/FilterGroup";
import DepartmentFilter, { DepartmentFilterState } from "components/filters/DepartmentFilter";
import LocationFilter, { LocationFilterState } from "components/filters/LocationFilter";
import ValueFilter, { ValueFilterState } from "components/filters/ValueFilter";

import RecognitionModal from "./RecognitionModal";
import RecognitionSlides from "./RecognitionSlides";

import FromFilter, { FromFilterState } from "./filters/FromFilter";
import RecognitionTypeFilter, { RecognitionTypeFilterState } from "./filters/RecognitionTypeFilter";

const RecognitionFilters = ({ onCreate, options, loading, pagination }) => {
  const { totalCount } = pagination || { totalCount: 0 };
  const { open: openOnMobile, onToggle: onToggleOpenOnMobile } = useDoor(false);
  const { onClearQueryString } = useQueryString();
  const isAFilterApplied = Object.entries(options).map(option => option[1])
    .filter(array => array).length > 0;

  const { departments } = useDepartments();
  const { locations } = useLocations();
  const { organizationValues } = useValues();
  const { recognitionTypes } = useRecognitionTypes();
  const labels = useFieldsLabels();

  const filters = {
    locations: getFiltersOptions("locationIds", locations, options),
    departments: getFiltersOptions("departmentIds", departments, options),
    organizationValues: getFiltersOptions("organizationValueIds", organizationValues, options),
    recognitionTypes: getFiltersOptions("recognitionTypeIds", recognitionTypes, options)
  };

  const recognitionsWord = totalCount === 1 ? "recognition" : "recognitions";
  const resultsText = totalCount ? `${t("Showing")} ${totalCount} ${recognitionsWord}` : t(`No ${recognitionsWord}`);

  const getFromFilter = () => {
    switch (options.type) {
      case "automatic":
        return ["CultureBot"];
      case "manual":
        return ["colleagues"];
      default:
        return [];
    }
  };

  return (
    <>
      <div className="Filters__button-wrapper row clearfix">
        <div className="Filters__button col-xs-6 col-sm-6 col-md-6 col-lg-12">
          <RecognitionModal onCreate={onCreate}>
            {onTrigger => (
              <PlainButton className="button create thumbs-up" onClick={onTrigger}>
                <Icon icon="megaphone-ol" />
                <span>
                  <Trans>Give Recognition</Trans>
                </span>
              </PlainButton>
            )}
          </RecognitionModal>
        </div>
        <div className="Filters__button toggle-filters col-xs-6 col-sm-6 col-md-6 col-lg-12">
          <PlainButton onClick={onToggleOpenOnMobile} className="button toggle-filters">
            <Icon icon="levels" />
            {
              openOnMobile
                ? <Trans>Hide Filters</Trans>
                : <Trans>Filter Results</Trans>
            }
          </PlainButton>
        </div>
      </div>
      <Panel className={openOnMobile ? "open" : "closed"}>
        <div className="chq-pan--hd">
          <RecognitionSlides options={options} />
          <h2>
            <Icon icon="levels" />
            {" "}
            <Trans>Filter Results</Trans>
          </h2>
          <hr />
        </div>
        <Panel.Body>
          <LocationFilter />
          <DepartmentFilter />
          <ValueFilter />
          <FromFilter />
          <RecognitionTypeFilter />
        </Panel.Body>
        <Panel.Footer className="Filters__Footer">
          {isAFilterApplied && !loading && (
            <>
              <div className="Filters__Footer--header">
                <h2 className="Filters__Applied"><Trans>Applied filters</Trans></h2>
                <PlainButton className="Filters__Footer--clear" onClick={onClearQueryString}>
                  <Trans>Clear</Trans>
                </PlainButton>
              </div>
              <hr />
              <div className="Filters__Active">
                <LocationFilterState />
                <DepartmentFilterState />
                <ValueFilterState />
                <FromFilterState />
                <RecognitionTypeFilterState />
              </div>
              <div className="Filters__Results">
                <p className="results" role="status">
                  <p>{resultsText}{" "}<Trans>that match your filters.</Trans></p>
                  <p>
                    <Trans>
                      You have selected recognitions filters that include the following:
                    </Trans>
                  </p>
                  {(options.type || filters.recognitionTypes.length > 0) && (
                    <p>
                      <Trans>Recognitions</Trans>
                      <FilterGroup key="from" group={getFromFilter()} prefix="from" />
                      <FilterGroup key="types" group={filters.recognitionTypes} prefix="of type" orRelation />
                    </p>
                  )}
                  <p>
                    <FilterGroup key="locations" group={filters.locations} prefix="Locations include" orRelation />
                  </p>
                  <p>
                    <FilterGroup
                      key={labels.departments.plural.toLowerCase()}
                      group={filters.departments}
                      prefix={`${labels.departments.plural} include`}
                      orRelation
                    />
                  </p>
                  <p>
                    <FilterGroup key="values" group={filters.organizationValues} prefix="Values include" orRelation />
                  </p>
                </p>
              </div>
            </>
          )}
        </Panel.Footer>
      </Panel>
    </>
  );
};

export default RecognitionFilters;
